/**
 * Отправляет в журнал ошибку, которую увидел пользователь:
 * эндпоинт, тело запроса и ответ бэка. Пароли и токены вырезаются.
 */
import axios from 'axios';
import { ACCESS_KEY, isCredentialRequest, isRefreshRequest, requestUrl } from './authSession';
import { toApiError } from './errors';
import { recoverFromStaleChunk } from '../utils/chunkLoadRecovery';
import { API_REQUEST_FAILED } from '@/constants/userMessages';

const MAX_BODY = 12000;
const MAX_MESSAGE = 2000;
const DEDUPE_MS = 10000;
const SENSITIVE_KEYS = new Set([
  'password',
  'old_password',
  'new_password',
  'new_password2',
  'password2',
  'token',
  'access',
  'refresh',
  'authorization',
  'secret',
  'access_token',
  'refresh_token',
  'csrfmiddlewaretoken',
]);

const recentReports = new Map();
let listenersInstalled = false;

function errorsUrl() {
  const base = (process.env.VUE_APP_API_BASE_URL || '').replace(/\/$/, '');
  return `${base}/api/admin/errors/`;
}

function isErrorLogRequest(config) {
  return /\/api\/admin\/errors\/?/i.test(requestUrl(config));
}

function clip(text, limit) {
  const value = String(text || '');
  return value.length > limit ? `${value.slice(0, limit)}...` : value;
}

function redactValue(value, depth = 0) {
  if (depth > 6) return '[…]';
  if (typeof FormData !== 'undefined' && value instanceof FormData) {
    const summary = {};
    value.forEach((item, key) => {
      if (SENSITIVE_KEYS.has(String(key).toLowerCase())) {
        summary[key] = '[скрыто]';
      } else if (typeof File !== 'undefined' && item instanceof File) {
        summary[key] = `[файл ${item.name}, ${item.size} байт]`;
      } else {
        summary[key] = redactValue(item, depth + 1);
      }
    });
    return summary;
  }
  if (Array.isArray(value)) {
    const head = value.slice(0, 40).map((item) => redactValue(item, depth + 1));
    if (value.length > 40) head.push(`… ещё ${value.length - 40}`);
    return head;
  }
  if (value && typeof value === 'object') {
    const redacted = {};
    Object.keys(value).slice(0, 60).forEach((key) => {
      redacted[key] = SENSITIVE_KEYS.has(String(key).toLowerCase())
        ? '[скрыто]'
        : redactValue(value[key], depth + 1);
    });
    if (Object.keys(value).length > 60) {
      redacted['…'] = `ещё ${Object.keys(value).length - 60}`;
    }
    return redacted;
  }
  return value;
}

function parseMaybeJson(value) {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed || (trimmed[0] !== '{' && trimmed[0] !== '[')) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

function stringifyForLog(value, limit) {
  const redacted = redactValue(parseMaybeJson(value));
  let text = '';
  try {
    text = typeof redacted === 'string' ? redacted : JSON.stringify(redacted, null, 2);
  } catch {
    text = String(redacted ?? '');
  }
  return clip(text, limit);
}

function endpointPath(config) {
  const raw = requestUrl(config);
  let pathname = raw;
  let existing = '';
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const parsed = new URL(raw, base);
    pathname = parsed.pathname;
    existing = parsed.search;
  } catch {
    return clip(raw, 2000);
  }
  const params = new URLSearchParams(existing);
  const extra = config && config.params;
  if (extra && typeof extra === 'object') {
    Object.entries(extra).forEach(([key, item]) => {
      if (item == null || params.has(key)) return;
      params.append(key, typeof item === 'object' ? JSON.stringify(item) : String(item));
    });
  }
  const pairs = [];
  params.forEach((item, key) => {
    const secret = SENSITIVE_KEYS.has(String(key).toLowerCase());
    const value = secret ? '[скрыто]' : encodeURIComponent(item);
    pairs.push(`${encodeURIComponent(key)}=${value}`);
  });
  return clip(`${pathname}${pairs.length ? `?${pairs.join('&')}` : ''}`, 2000);
}

function isBenignApiError(error) {
  const config = error && error.config;
  const status = error.response && error.response.status;
  const method = String((config && config.method) || '').toUpperCase();
  const url = config ? endpointPath(config) : '';
  const data = error.response ? error.response.data : null;
  const detail = data && typeof data.detail === 'string' ? data.detail : '';

  if (method === 'GET' && status === 404 && /\/calculator\/check_dvh\/?/.test(url)) {
    return true;
  }
  if (method === 'GET' && status === 404 && /\/calculator\/dvh_structures\/?/.test(url)) {
    return true;
  }
  if (method === 'POST' && status === 400 && /\/calculator\/adaptive_rt\/structures\/?/.test(url)) {
    const body = data && typeof data.error === 'string' ? data.error : '';
    if (/^Plan not found:/i.test(body)) return true;
  }
  if (
    method === 'POST'
    && status === 400
    && /\/user\/logout\/?/.test(url)
    && /blacklisted|token is invalid/i.test(detail)
  ) {
    return true;
  }
  return false;
}

function shortMessage(error) {
  const parsed = toApiError(error);
  const message = parsed && parsed.message ? String(parsed.message).trim() : '';
  if (message && message !== API_REQUEST_FAILED) return message;
  return (error && error.message) || API_REQUEST_FAILED;
}

function responseBody(error) {
  const data = error && error.response ? error.response.data : null;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    return `[бинарный ответ, ${data.size} байт]`;
  }
  if (data == null || data === '') return '';
  return stringifyForLog(data, MAX_BODY);
}

function requestBody(config) {
  if (!config || config.data == null || config.data === '') return '';
  return stringifyForLog(config.data, MAX_BODY);
}

function isDuplicate(key) {
  const now = Date.now();
  const previous = recentReports.get(key);
  if (previous && now - previous < DEDUPE_MS) return true;
  recentReports.set(key, now);
  if (recentReports.size > 80) {
    recentReports.forEach((ts, storedKey) => {
      if (now - ts > DEDUPE_MS) recentReports.delete(storedKey);
    });
  }
  return false;
}

function postErrorReport(payload) {
  let token = '';
  try {
    token = localStorage.getItem(ACCESS_KEY) || '';
  } catch {
    token = '';
  }
  if (!token) return;
  axios.create({ timeout: 15000 }).post(errorsUrl(), payload, {
    headers: { Authorization: `Bearer ${token}` },
  }).catch(() => {});
}

export function reportApiError(error) {
  if (!error || error.__reportedToErrorLog) return;
  if (axios.isCancel(error) || error.code === 'ERR_CANCELED') return;
  if (isBenignApiError(error)) return;
  const config = error.config;
  if (config && (isErrorLogRequest(config) || isRefreshRequest(config) || isCredentialRequest(config))) return;
  error.__reportedToErrorLog = true;

  const method = String((config && config.method) || '').toUpperCase();
  const url = config ? endpointPath(config) : '';
  const status = error.response && error.response.status ? error.response.status : null;
  const message = shortMessage(error);
  if (isDuplicate([method, url, status || '', message].join(' '))) return;

  postErrorReport({
    section: 'API',
    error_message: clip(message, MAX_MESSAGE),
    request_method: method,
    request_url: url,
    request_body: requestBody(config),
    response_body: responseBody(error),
    http_status: status,
    user_agent: typeof navigator !== 'undefined' ? clip(navigator.userAgent, 1000) : '',
    page_url: typeof window !== 'undefined' ? clip(window.location.href, 2000) : '',
  });
}

export function reportClientError(error, section) {
  const source = error instanceof Error ? error : new Error(clip(error, MAX_MESSAGE));
  if (source.__reportedToErrorLog) return;
  if (source.isAxiosError || source.response || source.config) {
    reportApiError(source);
    return;
  }
  source.__reportedToErrorLog = true;
  const message = source.message || 'Ошибка приложения';
  if (isDuplicate([section, message].join(' '))) return;
  postErrorReport({
    section,
    error_message: clip(message, MAX_MESSAGE),
    response_body: clip(source.stack || '', MAX_BODY),
    user_agent: typeof navigator !== 'undefined' ? clip(navigator.userAgent, 1000) : '',
    page_url: typeof window !== 'undefined' ? clip(window.location.href, 2000) : '',
  });
}

export function installClientErrorLogging() {
  if (listenersInstalled || typeof window === 'undefined') return;
  listenersInstalled = true;
  window.addEventListener('error', (event) => {
    const filename = String(event.filename || '');
    if (filename.startsWith('chrome-extension://')) return;
    const error = event.error || new Error(event.message || 'Ошибка JavaScript');
    if (recoverFromStaleChunk(error, filename)) {
      event.preventDefault();
      return;
    }
    reportClientError(error, 'JavaScript');
  });
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const error = reason instanceof Error ? reason : new Error(clip(reason, MAX_MESSAGE));
    if (recoverFromStaleChunk(error)) {
      event.preventDefault();
      return;
    }
    reportClientError(error, 'Promise');
  });
}
